/**
 * Adaptation coaching — the request a company sends to coach its talents.
 *
 * One talent (the Coach dialog, a quick nudge), a group the server chooses
 * over the whole roster (the inactive, the streaks at risk, the top performers
 * to celebrate, the active learners to challenge) or a whole engagement band
 * ("Coach all" in the Coaching Center). The server picks each talent's action
 * unless one is given, and sends the company's own message template for it —
 * or, with a personal note, that note.
 *
 * Error messages are i18n keys per the `@tonnie/schemas` convention.
 */

import { z } from 'zod';

/** The five coaching actions — `COACHING_ACTIONS` in @tonnie/heroes. */
export const coachingActionSchema = z.enum([
  'encourage', // Send encouragement
  'challenge', // Issue a stretch challenge
  'checkin', // Gentle check-in / "everything OK?"
  'celebrate', // Celebrate progress
  'remind', // Streak / activity reminder
]);

export type CoachingActionInput = z.infer<typeof coachingActionSchema>;

/** The groups the server chooses over the whole roster. */
export const COACHING_GROUPS = ['inactive', 'streak', 'celebrate', 'challenge'] as const;
export type CoachingGroup = (typeof COACHING_GROUPS)[number];

/** The engagement bands — `AdaptationEngagementLevel` in @tonnie/heroes. */
export const COACHING_BANDS = ['thriving', 'active', 'slowing', 'at_risk', 'inactive'] as const;

/** A personal note: optional, and when present 5–500 characters (trimmed). */
const personalNote = z
  .string()
  .trim()
  .min(5, { message: 'business.adaptationPage.errors.messageTooShort' })
  .max(500, { message: 'business.adaptationPage.errors.messageTooLong' });

/** `POST /business/adaptation/coaching` */
export const adaptationCoachingRequestSchema = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('talent'),
    talentId: z.uuid(),
    /** The engagement coached — a talent can hold several journeys here. */
    journeyId: z.uuid(),
    /** Omitted: the server picks the action that fits the talent. */
    action: coachingActionSchema.optional(),
    note: personalNote.optional(),
  }),
  z.object({ target: z.literal('group'), group: z.enum(COACHING_GROUPS) }),
  z.object({ target: z.literal('band'), band: z.enum(COACHING_BANDS) }),
]);
export type AdaptationCoachingRequest = z.infer<typeof adaptationCoachingRequestSchema>;

/** What a coaching request did: how many talents were coached. */
export interface AdaptationCoachingResult {
  sent: number;
}

/**
 * The Coach dialog's form: the action, and an optional personal note — empty
 * means "send the company's message for this action".
 */
export const coachTalentFormSchema = z.object({
  action: coachingActionSchema,
  note: z.union([z.literal(''), personalNote]),
});
export type CoachTalentFormInput = z.infer<typeof coachTalentFormSchema>;
